import { useEffect, useState, useSyncExternalStore } from "react";
import { getSocket, useSocketStatus } from "./socket";

/*
 * Connection awareness. `useOnline()` is the browser's network state; the realtime
 * connection (socket) is tracked separately because it can drop while the network
 * is fine (server restart) and lags behind when the network returns.
 */

function subscribeOnline(cb: () => void) {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
}

/** False while the device has no network. Chat uses it to disable sending. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

export type ConnectionState = "online" | "offline" | "connecting" | "reconnecting";

/**
 * Network + realtime connection in one value, with the reconnect attempt count.
 * "connecting" is the first connection; "reconnecting" follows a drop.
 */
export function useConnection(): { state: ConnectionState; attempt: number } {
  const online = useOnline();
  const status = useSocketStatus();
  const [attempt, setAttempt] = useState(0);
  const [everConnected, setEverConnected] = useState(false);

  useEffect(() => {
    if (status === "connected") {
      setEverConnected(true);
      setAttempt(0);
    }
  }, [status]);

  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const onAttempt = (n: number) => setAttempt(n);
    s.io.on("reconnect_attempt", onAttempt);
    return () => {
      s.io.off("reconnect_attempt", onAttempt);
    };
  }, [status]);

  if (!online) return { state: "offline", attempt };
  if (status === "connected") return { state: "online", attempt: 0 };
  return { state: everConnected ? "reconnecting" : "connecting", attempt };
}
